import { useEffect, useMemo, useState } from "react";
import { GripVertical, History, Plus, Search, SlidersHorizontal } from "lucide-react";
import { ShopifySyncBar } from "@/components/ShopifySyncBar";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ImageUploader, PageHeader, Tone } from "@/components/shared";
import { api, CATEGORIES, errMsg, fdt, inr, label, useApi } from "@/lib/api";

import { AdjustDialog, BatchList, LocationBreakdown } from "@/components/StockAdjust";
const COLS = {
  name: { title: "Name", render: (p) => <div><div className="font-medium">{p.name}</div><div className="text-[11px] text-muted-foreground font-mono">{p.sku}</div></div> },
  brand: { title: "Brand", render: (p) => p.brand },
  category: { title: "Category", render: (p) => <span className="text-muted-foreground">{label(p.category)}</span> },
  taxableValue: { title: "Taxable Value", render: (p) => <span className="num" data-testid={`taxable-${p.sku}`}>{p.taxableValue ? inr(p.taxableValue) : <span className="text-muted-foreground">Auto</span>}</span> },
  gst: { title: "GST", render: (p) => <span className="num">{p.taxRatePercent}%</span> },
  stock: { title: "Stock Qty", render: (p) => <div><Tone tone={p.stockQuantity === 0 ? "red" : p.stockQuantity <= 3 ? "amber" : "green"} data-testid={`stock-${p.sku}`}>Total: {p.stockQuantity}</Tone><LocationBreakdown p={p} /></div> },
  batches: { title: "FIFO Batches (qty @ price)", render: (p) => <BatchList p={p} /> },
  shopify: { title: "Shopify Status", render: (p) => p.shopifyProductId ? <Tone tone="green">Synced</Tone> : <Tone tone="gray">Not linked</Tone> },
  updatedAt: { title: "Last Updated", render: (p) => <span className="text-xs text-muted-foreground">{fdt(p.updatedAt)}</span> },
};
const DEFAULT_ORDER = Object.keys(COLS);

function ProductDialog({ open, product, onClose, onSaved }) {
  const empty = { sku: "", name: "", brand: "", category: "CPU", purchasePrice: "", taxableValue: "", taxRatePercent: 18, stockQuantity: 0, photos: [] };
  const [f, setF] = useState(empty);
  useEffect(() => { if (open) setF(product ? { ...empty, ...product, taxableValue: product.taxableValue ?? "" } : empty); }, [open, product]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const body = { ...f, purchasePrice: +f.purchasePrice, taxRatePercent: +f.taxRatePercent, stockQuantity: +f.stockQuantity,
      taxableValue: f.taxableValue === "" ? null : +f.taxableValue };
    try {
      if (product) await api.put(`/inventory/${product.id}`, body); else await api.post("/inventory", body);
      toast.success(product ? "Product updated" : "Product added");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fld = (k, l, t = "text") => (
    <div className="space-y-1.5"><Label>{l}</Label><Input type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`product-${k}-input`} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="add-product-dialog">
        <DialogHeader><DialogTitle>{product ? `Edit ${product.name}` : "Add product"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {fld("sku", "SKU")}{fld("brand", "Brand")}
          <div className="col-span-2">{fld("name", "Product name")}</div>
          <div className="space-y-1.5"><Label>Category</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{label(c)}</SelectItem>)}</SelectContent>
            </Select></div>
          {fld("purchasePrice", "Purchase price (₹)", "number")}{fld("taxableValue", "Taxable sales value (₹)", "number")}{fld("taxRatePercent", "GST %", "number")}
          {fld("stockQuantity", product ? "Stock quantity" : "Opening stock", "number")}
          {product && +f.stockQuantity !== product.stockQuantity && <div className="col-span-2 text-xs text-amber-600" data-testid="stock-edit-notice">Stock change of {+f.stockQuantity - product.stockQuantity} will be logged as "Manual Edit".</div>}
          <div className="col-span-2 space-y-1.5"><Label>Photos</Label><ImageUploader images={f.photos} onChange={(p) => setF({ ...f, photos: p })} testid="product-photos" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="product-save-button">{product ? "Save changes" : "Save product"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistorySheet({ product, onClose }) {
  const { data } = useApi(product ? `/inventory/${product.id}/history` : null);
  return (
    <Sheet open={!!product} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="history-drawer">
        <SheetHeader><SheetTitle>{product?.name}</SheetTitle></SheetHeader>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="rounded-lg border p-4"><div className="text-xs text-muted-foreground mb-1.5">Active FIFO batches</div><BatchList p={product || {}} /></div>
          <div className="rounded-lg border p-4"><div className="text-xs text-muted-foreground">Current Stock</div>
            <div className="text-xl font-semibold num mt-1">{product?.stockQuantity}</div></div>
        </div>
        <div className="mt-6 space-y-2" data-testid="history-list">
          {data?.history.map((h) => (
            <div key={h.id} className="flex items-center gap-3 rounded-md border px-3 py-2.5 text-sm">
              <span className={`num font-semibold w-12 ${h.change > 0 ? "text-emerald-600" : "text-rose-600"}`}>{h.change > 0 ? "+" : ""}{h.change}</span>
              <div className="flex-1 min-w-0"><div>{h.reason}</div><div className="text-[11px] text-muted-foreground">{h.loggedBy} · {fdt(h.createdAt)}</div></div>
              <div className="text-right"><div className="num text-xs">{inr(h.unitCost)}/u</div><div className="text-[11px] text-muted-foreground">Bal {h.balanceAfter}</div></div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Inventory() {
  const { data, reload, setData } = useApi("/inventory");
  const applyStock = (id, change, server) => setData((list) => list.map((p) => (p.id !== id ? p
    : server?.id ? { ...p, ...server } : { ...p, stockQuantity: p.stockQuantity + change })));
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");
  const [brand, setBrand] = useState("ALL");
  const [order, setOrder] = useState(() => {
    const s = JSON.parse(localStorage.getItem("gn_inv_cols") || "null");
    return s && s.length === DEFAULT_ORDER.length && s.every((k) => COLS[k]) ? s : DEFAULT_ORDER;
  });
  const [drag, setDrag] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [hist, setHist] = useState(null);
  useEffect(() => localStorage.setItem("gn_inv_cols", JSON.stringify(order)), [order]);
  const brands = useMemo(() => [...new Set((data || []).map((p) => p.brand).filter(Boolean))].sort(), [data]);
  const rows = (data || []).filter((p) => (cat === "ALL" || p.category === cat) && (brand === "ALL" || p.brand === brand) &&
    `${p.name} ${p.sku} ${p.brand}`.toLowerCase().includes(q.toLowerCase()));
  const onDrop = (target) => {
    if (!drag || drag === target) return;
    const next = order.filter((k) => k !== drag);
    next.splice(next.indexOf(target), 0, drag);
    setOrder(next);
    setDrag(null);
  };
  return (
    <div>
      <PageHeader title="Inventory" subtitle={`${rows.length} products · drag column headers to rearrange`}>
        <ShopifySyncBar onSynced={reload} />
        <Button onClick={() => setAdding(true)} data-testid="add-product-button"><Plus className="h-4 w-4 mr-2" />Add product</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative w-full sm:w-72"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search name, SKU, brand" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inventory-search-input" /></div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="flex-1 sm:flex-none sm:w-48" data-testid="inventory-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{label(c)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="flex-1 sm:flex-none sm:w-40" data-testid="inventory-brand-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All brands</SelectItem>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={() => setOrder(DEFAULT_ORDER)} data-testid="reset-columns-button"><SlidersHorizontal className="h-4 w-4 mr-2" />Reset columns</Button>
      </div>
      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="inventory-table">
          <thead>
            <tr className="border-b bg-muted/40">
              {order.map((k) => (
                <th key={k} draggable onDragStart={() => setDrag(k)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(k)}
                  className={`text-left font-medium text-xs text-muted-foreground px-4 py-2.5 cursor-grab select-none whitespace-nowrap ${drag === k ? "opacity-40" : ""}`}
                  data-testid={`col-header-${k}`}>
                  <span className="inline-flex items-center gap-1"><GripVertical className="h-3 w-3 opacity-50" />{COLS[k].title}</span>
                </th>
              ))}
              <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b last:border-0 hover:bg-secondary/30 transition-colors" data-testid={`inventory-row-${p.sku}`}>
                {order.map((k) => <td key={k} className="px-4 py-3">{COLS[k].render(p)}</td>)}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-product-${p.sku}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => setAdjust(p)} data-testid={`adjust-stock-${p.sku}`}>Adjust</Button>
                  <Button size="sm" variant="ghost" onClick={() => setHist(p)} data-testid={`history-${p.sku}`}><History className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ProductDialog open={adding || !!editing} product={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={reload} />
      <AdjustDialog product={adjust} onClose={() => setAdjust(null)} onSaved={(id, change, server) => { applyStock(id, change, server); reload(); }} />
      <HistorySheet product={hist} onClose={() => setHist(null)} />
    </div>
  );
}
